import { displayText } from '../../../terminology/store';
import { useTerminology } from '../../../terminology/TerminologyContext';
// Clients.jsx
import React, { useState, useEffect, useContext, useRef, useCallback } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import 'bootstrap-icons/font/bootstrap-icons.css';
import api from '../../services/api';
import ThemeContext from '../../ThemeContext';
import Pagination from '../../Utils/Pagination';
import ResellerConfig from '../../../config/ResellerConfig';
import { URL_SEGMENTS } from '../../../config/Routesconfig';

const Clients = () => {
  useTerminology();
  const { theme }    = useContext(ThemeContext);
  const { username } = useParams();
  const navigate     = useNavigate();

  // ── Config ──────────────────────────────────────────────────────────────
  const cfg           = ResellerConfig.clients;
  const enterpriseCfg = ResellerConfig.enterpriseClient;

  // ── Theme ────────────────────────────────────────────────────────────────
  const fontFamily    = theme.typography?.fontFamily    || '"Roboto", "Helvetica Neue", Arial, sans-serif';
  const bodyFontSize  = theme.typography?.bodyFontSize  || 14;
  const titleFontSize = theme.typography?.titleFontSize || 24;

  // ── Responsive flag (avoid window.innerWidth in render) ──────────────────
  const [isMobile, setIsMobile] = useState(window.innerWidth < 600);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 600);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  // ── Auth — read once, not on every render ────────────────────────────────
  const [currentUser] = useState(() => JSON.parse(localStorage.getItem('user')) || {});
  const isSuperAdmin  = currentUser.role === 'superadmin';
  const isAdmin       = currentUser.role === 'admin';
  const isReseller    = currentUser.role === 'reseller';

  // ── State ─────────────────────────────────────────────────────────────────
  const [clients,             setClients]             = useState([]);
  const [isLoading,           setIsLoading]           = useState(false);
  const [searchTerm,          setSearchTerm]          = useState('');
  const [highlightedClientId, setHighlightedClientId] = useState(null);
  const [highlightedField,    setHighlightedField]    = useState(null);
  const [hoveredClientId,     setHoveredClientId]     = useState(null);
  const searchInputRef = useRef(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage,     setPerPage]     = useState(10);
  const [gotoPage,    setGotoPage]    = useState('');
  const [showModal,        setShowModal]        = useState(false);
  const [selectedClientId, setSelectedClientId] = useState(null);
  const [actionType,       setActionType]       = useState('');
  const [sortField, setSortField] = useState('client_name');
  const [sortOrder, setSortOrder] = useState('asc');

  // ── Fetch clients (runs once) ─────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    const fetchClients = async () => {
      setIsLoading(true);
      try {
        const response    = await api.get('/client-admins');
        const clientsData = Array.isArray(response.data.data) ? response.data.data : [];
        if (!cancelled) setClients(clientsData);
      } catch {
        if (!cancelled) {
          toast.error(`Failed to fetch ${cfg.plural.toLowerCase()}.`);
          setClients([]);
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    fetchClients();
    return () => { cancelled = true; };
  }, []); // ← no deps — intentionally runs only once on mount

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handleActivateDeactivate = useCallback((clientId, currentStatus) => {
    setSelectedClientId(clientId);
    setActionType(currentStatus === 'Active' ? 'deactivate' : 'activate');
    setShowModal(true);
  }, []);

  const confirmAction = useCallback(async () => {
    const clientId      = selectedClientId;
    const currentStatus = clients.find((c) => c.id === clientId)?.client_setting;
    if (!currentStatus) return;
    const newStatus = currentStatus === 'Active' ? 'Deactivated' : 'Active';

    // Optimistic update
    setClients((prev) => prev.map((c) => c.id === clientId ? { ...c, client_setting: newStatus } : c));
    setIsLoading(true);
    try {
      await api.put(`/client-admins/${clientId}`, { client_setting: newStatus });
      toast.success(`${cfg.label} ${newStatus.toLowerCase()} successfully.`);
    } catch {
      // Roll back on failure
      setClients((prev) => prev.map((c) => c.id === clientId ? { ...c, client_setting: currentStatus } : c));
      toast.error(`Failed to update ${cfg.label.toLowerCase()} status.`);
    } finally {
      setIsLoading(false);
      setShowModal(false);
      setSelectedClientId(null);
      setActionType('');
    }
  }, [selectedClientId, clients, cfg.label]);

  const handleAddClient = useCallback(() => {
    if (isSuperAdmin)
      navigate(`/${URL_SEGMENTS.superadmin}/${URL_SEGMENTS.addClient}`);
    else if (isAdmin)
      navigate(`/${URL_SEGMENTS.admin}/${URL_SEGMENTS.addClient}`);
    else
      navigate(`/${currentUser.username || username}/${URL_SEGMENTS.addClient}`);
  }, [isSuperAdmin, isAdmin, currentUser.username, username, navigate]);

  const handleSearch = useCallback((e) => {
    setSearchTerm(e.target.value);
    setHighlightedClientId(null);
    setHighlightedField(null);
    setCurrentPage(1);
  }, []);

  const handleSearchSubmit = useCallback(() => {
    const term  = searchTerm.toLowerCase();
    const match = clients.find((c) =>
      (c.id?.toString() || '') === searchTerm ||
      (c.client_name  || '').toLowerCase().includes(term) ||
      (c.primary_name || '').toLowerCase().includes(term)
    );
    if (match) {
      setHighlightedClientId(match.id);
      if      ((match.id?.toString() || '') === searchTerm)             setHighlightedField('id');
      else if ((match.client_name  || '').toLowerCase().includes(term)) setHighlightedField('client_name');
      else if ((match.primary_name || '').toLowerCase().includes(term)) setHighlightedField('primary_name');
    } else {
      setHighlightedClientId(null);
      setHighlightedField(null);
    }
  }, [searchTerm, clients]);

  const handleKeyPress = useCallback((e) => {
    if (e.key === 'Enter') handleSearchSubmit();
  }, [handleSearchSubmit]);

  const handleSort = useCallback((field) => {
    setSortField((prev) => {
      setSortOrder(prev === field && sortOrder === 'asc' ? 'desc' : 'asc');
      return field;
    });
  }, [sortOrder]);

  // ── Sort helpers ──────────────────────────────────────────────────────────
  const getSortValue = useCallback((client, field) => {
    if (field === 'id')         return client.id;
    if (field === 'start_date') return client.start_date ? new Date(client.start_date) : null;
    const map = {
      client_name   : client.client_name    || 'N/A',
      primary_name  : client.primary_name   || 'N/A',
      client_setting: client.client_setting || 'N/A',
    };
    const val = map[field] ?? client[field] ?? 'N/A';
    return typeof val === 'string' ? val.toLowerCase() : val;
  }, []);

  // ── Filtered + sorted list ─────────────────────────────────────────────────
  const filteredClients = clients
    .filter((c) =>
      (c.id?.toString() || '').includes(searchTerm) ||
      (c.client_name  || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (c.primary_name || '').toLowerCase().includes(searchTerm.toLowerCase())
    )
    .sort((a, b) => {
      const vA = getSortValue(a, sortField);
      const vB = getSortValue(b, sortField);
      let cmp;
      if (vA instanceof Date && vB instanceof Date) cmp = vA.getTime() - vB.getTime();
      else if (vA === null && vB === null) cmp = 0;
      else if (vA === null) cmp = 1;
      else if (vB === null) cmp = -1;
      else cmp = vA < vB ? -1 : vA > vB ? 1 : 0;
      return sortOrder === 'asc' ? cmp : -cmp;
    });

  const paginatedClients = filteredClients.slice((currentPage - 1) * perPage, currentPage * perPage);

  const hlStyle = useCallback((client, field) => ({
    backgroundColor:
      client.id === highlightedClientId && highlightedField === field ? '#FFD700' : '#fff',
    fontFamily,
  }), [highlightedClientId, highlightedField, fontFamily]);

  const sortIcon = (field) =>
    sortField === field
      ? <i className={`bi ${sortOrder === 'asc' ? 'bi-arrow-up' : 'bi-arrow-down'} ms-1`} />
      : null;

  // ── Edit path helper ───────────────────────────────────────────────────────
  const editPath = useCallback((clientId) =>
    isSuperAdmin
      ? `/${URL_SEGMENTS.superadmin}/${URL_SEGMENTS.editClient.replace('/:id', '')}/${clientId}`
      : isAdmin
      ? `/${URL_SEGMENTS.admin}/${URL_SEGMENTS.editClient.replace('/:id', '')}/${clientId}`
      : `/${currentUser.username || username}/${URL_SEGMENTS.editClient.replace('/:id', '')}/${clientId}`,
  [isSuperAdmin, isAdmin, currentUser.username, username]);

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div style={{ fontFamily, fontSize: `${bodyFontSize}px` }}>
      <div style={{
        backgroundColor: '#fff', padding: '1rem', borderRadius: '10px',
        boxShadow: '0 4px 20px rgba(0,0,0,0.15)', fontFamily, marginTop: '10px',
      }}>
        <ToastContainer />

        {/* Loading spinner */}
        {isLoading && (
          <div style={{
            position: 'fixed', top: '50%', left: '50%',
            transform: 'translate(-50%, -50%)', zIndex: 1050,
          }}>
            <div style={{
              width: '40px', height: '40px',
              border: `4px solid ${theme.colors.primary}`,
              borderTop: '4px solid transparent',
              borderRadius: '50%', animation: 'spin 1s linear infinite',
            }} />
          </div>
        )}

        {/* ── HEADER ─────────────────────────────────────────────────────── */}
        <div style={{
          display      : 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          alignItems   : isMobile ? 'stretch' : 'center',
          justifyContent: 'space-between',
          marginBottom : '1rem',
          gap          : '0.5rem',
        }}>
          <h4 style={{
            fontSize: `${titleFontSize}px`, fontWeight: 'bold',
            marginBottom: 0, color: theme.colors.textPrimary, fontFamily,
          }}>
            {ResellerConfig.pageTitles.clientList}
          </h4>

          {(isSuperAdmin || isReseller || isAdmin) && (
            <div style={{
              display       : 'flex',
              alignItems    : 'center',
              gap           : '0.5rem',
              flexWrap      : 'wrap',
              justifyContent: isMobile ? 'stretch' : 'flex-end',
            }}>
              {/* Search */}
              <div style={{ position: 'relative', maxWidth: '140px', height: '38px' }}>
                <input
                  type="text"
                  placeholder={`Search ${cfg.plural.toLowerCase()}...`}
                  value={searchTerm}
                  onChange={handleSearch}
                  onKeyPress={handleKeyPress}
                  ref={searchInputRef}
                  style={{
                    width: '100%', height: '100%',
                    border: '1px solid #dee2e6', borderRadius: '25px',
                    outline: 'none', padding: '0 40px 0 14px',
                    fontSize: '12px', backgroundColor: '#fff', fontFamily,
                  }}
                />
                <button
                  onClick={handleSearchSubmit}
                  style={{
                    position: 'absolute', right: '2px', top: '2px',
                    width: '34px', height: '34px',
                    background: theme.gradients?.primary || theme.colors.primary,
                    border: 'none', borderRadius: '50%',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    cursor: 'pointer',
                  }}
                >
                  <i className="bi bi-search" style={{ color: '#fff', fontSize: '14px' }} />
                </button>
              </div>

              {/* Add Client button */}
              <button
                data-testid="add-client"
                style={{
                  background  : theme.gradients?.secondary || theme.colors.secondary,
                  color       : theme.colors.textWhite,
                  padding     : '6px 12px',
                  borderRadius: '8px',
                  fontSize    : 'clamp(10px,2.5vw,11px)',
                  border      : 'none',
                  minWidth    : '120px',
                  cursor      : 'pointer',
                  fontFamily,
                }}
                onClick={handleAddClient}
              >
                Add {cfg.label}
              </button>

              {/* Add Enterprise Client button (reseller only) */}
              {isReseller && (
                <Link
                  to={`/${currentUser.username || username}/enterprise-client-add`}
                  data-testid="add-enterprise-client"
                  style={{
                    background    : theme.gradients?.secondary || theme.colors.secondary,
                    color         : theme.colors.textWhite,
                    padding       : '6px 12px',
                    borderRadius  : '8px',
                    fontSize      : 'clamp(10px,2.5vw,11px)',
                    textDecoration: 'none',
                    minWidth      : '120px',
                    textAlign     : 'center',
                    fontFamily,
                  }}
                >
                  Add {enterpriseCfg.label}
                </Link>
              )}
            </div>
          )}
        </div>

        <Pagination
          currentPage={currentPage} setCurrentPage={setCurrentPage}
          perPage={perPage}         setPerPage={setPerPage}
          totalItems={filteredClients.length}
          gotoPage={gotoPage}       setGotoPage={setGotoPage}
        />

        {/* ── TABLE ──────────────────────────────────────────────────────── */}
        <div style={{
          overflowX: 'auto', backgroundColor: '#fff', borderRadius: '10px',
          border: '1px solid #dee2e6', marginTop: '1rem', marginBottom: '1rem',
        }}>
          <table style={{
            width: '100%', borderCollapse: 'collapse',
            fontSize: `${bodyFontSize}px`, fontFamily,
          }}>
            <thead>
              <tr style={{ backgroundColor: theme.colors.primary, color: theme.colors.textWhite }}>
                <th className="p-2" style={{ cursor: 'pointer' }} onClick={() => handleSort('id')}>
                  <span style={{ display: 'inline-flex', alignItems: 'center' }}>ID {sortIcon('id')}</span>
                </th>
                <th className="p-2" style={{ cursor: 'pointer' }} onClick={() => handleSort('client_name')}>
                  <span style={{ display: 'inline-flex', alignItems: 'center' }}>{cfg.label} Name {sortIcon('client_name')}</span>
                </th>
                <th className="p-2" style={{ cursor: 'pointer' }} onClick={() => handleSort('primary_name')}>
                  <span style={{ display: 'inline-flex', alignItems: 'center' }}>Primary Contact {sortIcon('primary_name')}</span>
                </th>
                <th className="p-2" style={{ cursor: 'pointer' }} onClick={() => handleSort('start_date')}>
                  <span style={{ display: 'inline-flex', alignItems: 'center' }}>{cfg.label} Since {sortIcon('start_date')}</span>
                </th>
                <th className="p-2" style={{ cursor: 'pointer' }} onClick={() => handleSort('client_setting')}>
                  <span style={{ display: 'inline-flex', alignItems: 'center' }}>Status {sortIcon('client_setting')}</span>
                </th>
                {(isSuperAdmin || isReseller || isAdmin) && (
                  <th className="p-2" style={{ textAlign: 'right' }}>Actions</th>
                )}
              </tr>
            </thead>
            <tbody>
              {paginatedClients.length > 0 ? (
                paginatedClients.map((client) => (
                  <tr
                    key={client.id}
                    onMouseEnter={() => setHoveredClientId(client.id)}
                    onMouseLeave={() => setHoveredClientId(null)}
                    style={{ borderBottom: '1px solid #dee2e6' }}
                  >
                    <td className="p-2" style={hlStyle(client, 'id')}>{client.id}</td>
                    <td className="p-2" style={hlStyle(client, 'client_name')}>{client.client_name || 'N/A'}</td>
                    <td className="p-2" style={hlStyle(client, 'primary_name')}>{client.primary_name || 'N/A'}</td>
                    <td className="p-2" style={{ fontFamily }}>
                      {client.start_date ? new Date(client.start_date).toLocaleDateString() : 'N/A'}
                    </td>
                    <td className="p-2" style={{ fontFamily }}>
                      <span style={{
                        color     : client.client_setting === 'Active' ? theme.colors.success : theme.colors.danger,
                        fontWeight: 'bold',
                      }}>
                        {client.client_setting || 'N/A'}
                      </span>
                    </td>
                    {(isSuperAdmin || isReseller || isAdmin) && (
                      <td className="p-2 position-relative">
                        {hoveredClientId === client.id && (
                          <div style={{
                            position : 'absolute',
                            right    : '0.5rem',
                            top      : '50%',
                            transform: 'translateY(-50%)',
                            display  : 'flex',
                            gap      : '0.5rem',
                          }}>
                            <Link
                              to={editPath(client.id)}
                              style={{
                                color         : theme.colors.primary,
                                textDecoration: 'underline',
                                fontSize      : `${bodyFontSize}px`,
                                fontFamily,
                              }}
                            >
                              Edit
                            </Link>
                            <button
                              onClick={() => handleActivateDeactivate(client.id, client.client_setting)}
                              style={{
                                background    : 'none',
                                border        : 'none',
                                color         : client.client_setting === 'Active'
                                  ? theme.colors.danger
                                  : theme.colors.success,
                                textDecoration: 'underline',
                                cursor        : 'pointer',
                                fontSize      : `${bodyFontSize}px`,
                                padding       : 0,
                                fontFamily,
                              }}
                            >
                              {client.client_setting === 'Active' ? 'Deactivate' : 'Activate'}
                            </button>
                          </div>
                        )}
                      </td>
                    )}
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    className="text-center p-3"
                    colSpan={isSuperAdmin || isReseller || isAdmin ? 6 : 5}
                    style={{ fontFamily }}
                  >
                    {searchTerm
                      ? `No ${cfg.label.toLowerCase()} found with ID, name, or contact "${searchTerm}"`
                      : cfg.emptyMessage}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <Pagination
          currentPage={currentPage} setCurrentPage={setCurrentPage}
          perPage={perPage}         setPerPage={setPerPage}
          totalItems={filteredClients.length}
          gotoPage={gotoPage}       setGotoPage={setGotoPage}
        />

        {/* ── CONFIRM MODAL ──────────────────────────────────────────────── */}
        {showModal && (
          <div style={{
            position       : 'fixed',
            inset          : 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display        : 'flex',
            alignItems     : 'center',
            justifyContent : 'center',
            zIndex         : 2000,
          }}>
            <div style={{
              backgroundColor: '#fff', padding: '20px',
              borderRadius: '8px', width: '320px',
              textAlign: 'center', fontFamily,
            }}>
              <h5 style={{
                marginBottom: '10px', color: theme.colors.danger,
                fontSize: `${titleFontSize}px`, fontFamily,
              }}>
                Confirm Action
              </h5>
              <p style={{ fontSize: `${bodyFontSize}px`, fontFamily }}>
                {ResellerConfig.modal[actionType === 'activate' ? 'confirmActivate' : 'confirmDeactivate'](cfg.label)}
              </p>
              <div style={{ display: 'flex', justifyContent: 'center', gap: '10px' }}>
                <button
                  onClick={() => setShowModal(false)}
                  style={{
                    backgroundColor: theme.colors.secondary,
                    color          : theme.colors.textWhite,
                    border         : 'none',
                    padding        : '8px 16px',
                    borderRadius   : '5px',
                    fontFamily,
                    fontSize       : `${bodyFontSize}px`,
                  }}
                >
                  No
                </button>
                <button
                  onClick={confirmAction}
                  style={{
                    backgroundColor: theme.colors.danger,
                    color          : theme.colors.textWhite,
                    border         : 'none',
                    padding        : '8px 16px',
                    borderRadius   : '5px',
                    fontFamily,
                    fontSize       : `${bodyFontSize}px`,
                  }}
                >
                  Yes
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Clients;